import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import useAuth from '../hooks/useAuth';

const TIPOS = ['Mixto', 'Fibra', 'Inalámbrico', 'Metro', 'Otro'];

const EMPTY_FORM = {
    nombre: '',
    direccion: '',
    coordenadas: '',
    tipo: 'Mixto',
    descripcion: '',
    pais: 'Colombia',
    departamento: 'Valle del Cauca',
    ciudad: '',
    activo: true
};

const TIPO_COLOR = {
    'Mixto': 'bg-purple-100 text-purple-700',
    'Fibra': 'bg-blue-100 text-blue-700',
    'Inalámbrico': 'bg-green-100 text-green-700',
    'Metro': 'bg-yellow-100 text-yellow-700',
    'Otro': 'bg-gray-100 text-gray-500'
};

const Nodos = () => {
    const { can, user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/infraestructura/nodos');
            setItems(data);
        } catch {
            setError('Error cargando nodos');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (user) loadData(); }, [loadData, user]);

    const showSuccess = (msg) => {
        setSuccess(msg); setError('');
        setTimeout(() => setSuccess(''), 4000);
    };

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setEditingId(null);
        setError('');
        setModalOpen(true);
    };

    const openEdit = (item) => {
        setForm({
            nombre: item.nombre || '',
            direccion: item.direccion || '',
            coordenadas: item.coordenadas || '',
            tipo: item.tipo || 'Mixto',
            descripcion: item.descripcion || '',
            pais: item.pais || 'Colombia',
            departamento: item.departamento || 'Valle del Cauca',
            ciudad: item.ciudad || '',
            activo: !!Number(item.activo)
        });
        setEditingId(item.id);
        setError('');
        setModalOpen(true);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError('');
        try {
            if (editingId) {
                await api.put(`/infraestructura/nodos/${editingId}`, form);
                showSuccess('Nodo actualizado correctamente');
            } else {
                await api.post('/infraestructura/nodos', form);
                showSuccess('Nodo creado correctamente');
            }
            setModalOpen(false);
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error guardando nodo');
        } finally {
            setSaving(false);
        }
    };

    const filtered = items.filter(i =>
        `${i.nombre} ${i.ciudad} ${i.tipo}`.toLowerCase().includes(search.toLowerCase())
    );

    const inputClass = "w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1";

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Nodos de Red</h2>
                {can('inf_nodos', 'create') && (
                    <button onClick={openCreate} className="text-white text-sm font-semibold px-5 py-2.5 rounded-lg" style={{ backgroundColor: 'var(--color-primary)' }}>
                        + Agregar Nuevo
                    </button>
                )}
            </div>

            {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4">{success}</div>}
            {error && !modalOpen && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

            <div className="mb-4">
                <input type="text" placeholder="Buscar nodo..." value={search} onChange={e => setSearch(e.target.value)}
                    className="w-full max-w-sm border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none" />
            </div>

            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-400 text-sm">Cargando...</div>
                ) : filtered.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">No se encontraron nodos</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                            <tr>
                                <th className="px-4 py-3 text-left">Nombre</th>
                                <th className="px-4 py-3 text-left">Tipo</th>
                                <th className="px-4 py-3 text-left">Dirección</th>
                                <th className="px-4 py-3 text-left">Ciudad</th>
                                <th className="px-4 py-3 text-left">Equipos</th>
                                {can('inf_nodos', 'update') && <th className="px-4 py-3 text-right">Acciones</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filtered.map(item => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 font-medium text-gray-800">{item.nombre}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TIPO_COLOR[item.tipo] || 'bg-gray-100 text-gray-500'}`}>
                                            {item.tipo}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-500">{item.direccion || '—'}</td>
                                    <td className="px-4 py-3 text-gray-500">{item.ciudad || '—'}</td>
                                    <td className="px-4 py-3 text-gray-500 text-xs">
                                        <span className="mr-2">🖥️ {item.mikrotik_count} MikroTik</span>
                                        <span className="mr-2">📡 {item.olt_count} OLT</span>
                                        <span>🌐 {item.ip_pool_count} IP Pool</span>
                                    </td>
                                    {can('inf_nodos', 'update') && (
                                        <td className="px-4 py-3 text-right">
                                            <button onClick={() => openEdit(item)}
                                                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">
                                                Editar
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">{editingId ? 'Editar nodo' : 'Nuevo nodo'}</h3>
                            <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <div className="p-6 space-y-4">
                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{error}</div>}

                            <div>
                                <label className={labelClass}>Nombre *</label>
                                <input name="nombre" value={form.nombre} onChange={handleChange} className={inputClass} placeholder="Ej: BOLO PCQ F.O" />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Tipo de nodo</label>
                                    <select name="tipo" value={form.tipo} onChange={handleChange} className={inputClass}>
                                        {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Ciudad</label>
                                    <input name="ciudad" value={form.ciudad} onChange={handleChange} className={inputClass} placeholder="Ej: Palmira" />
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Dirección</label>
                                <input name="direccion" value={form.direccion} onChange={handleChange} className={inputClass} placeholder="Ej: Bolo San Isidro" />
                            </div>

                            <div>
                                <label className={labelClass}>Coordenadas GPS</label>
                                <input name="coordenadas" value={form.coordenadas} onChange={handleChange} className={inputClass} placeholder="Ej: 3.5394,-76.3036" />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>País</label>
                                    <input name="pais" value={form.pais} onChange={handleChange} className={inputClass} />
                                </div>
                                <div>
                                    <label className={labelClass}>Departamento</label>
                                    <input name="departamento" value={form.departamento} onChange={handleChange} className={inputClass} />
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Descripción</label>
                                <textarea name="descripcion" value={form.descripcion} onChange={handleChange} rows={2} className={inputClass} />
                            </div>

                            {editingId && (
                                <div className="flex items-center gap-2">
                                    <input type="checkbox" name="activo" checked={form.activo} onChange={handleChange} id="activo_nodo" />
                                    <label htmlFor="activo_nodo" className="text-sm text-gray-700">Nodo activo</label>
                                </div>
                            )}
                        </div>
                        <div className="px-6 pb-6 flex gap-3">
                            <button onClick={() => setModalOpen(false)} className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm">Cancelar</button>
                            <button onClick={handleSubmit} disabled={saving}
                                className="flex-1 text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
                                style={{ backgroundColor: 'var(--color-primary)' }}>
                                {saving ? 'Guardando...' : (editingId ? 'Guardar cambios' : 'Crear nodo')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Nodos;
